import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, Table, Tag, Button, Tooltip, Spin, message, Row, Col } from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  DollarOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  TeamOutlined,
  CheckCircleOutlined,
  LoadingOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import axios from "axios";
import { JobDetailsWrapper } from "./AdminJobDetails.style";
import { API_CONST } from "../../../../const";

const AdminJobHiredTalents = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [jobData, setJobData] = useState(null);
  const [talents, setTalents] = useState([]);
  const [loading, setLoading] = useState(true);

  const formatCurrency = (value) => {
    if (!value && value !== 0) return "-";
    return `₹ ${Number(value).toLocaleString("en-IN")}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'open': return 'green';
      case 'closed': return 'red';
      case 'filled': return 'blue';
      case 'draft': return 'orange';
      default: return 'default';
    }
  };

  const handleDownloadResume = async (resumeUrl, talentName) => {
    if (!resumeUrl) {
      message.warning("No resume available for this talent");
      return;
    }
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.post(
        API_CONST.GET_DOCUMENT_URL,
        { objectPath: resumeUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.downloadUrl) {
        const link = document.createElement("a");
        link.href = response.data.downloadUrl;
        link.download = `${talentName}_Resume.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success("Resume download started");
      } else {
        message.error("Could not get download URL");
      }
    } catch (error) {
      console.error("Error downloading resume:", error);
      message.error("Failed to download resume");
    }
  };

  const fetchJobAndHiredTalents = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const headers = { Authorization: `Bearer ${token}` };

      const [jobResponse, hiringResponse] = await Promise.all([
        axios.get(`${API_CONST.GET_JOB_REQUIREMENT}/${jobId}`, { headers }),
        axios.post(API_CONST.GET_HIRING_RECORDS, { jobId }, { headers }),
      ]);

      if (jobResponse.data?.job) {
        const job = jobResponse.data.job;
        setJobData({
          id: job.id,
          role: job.role,
          status: job.status,
          engagementType: job.engagementType || "Contract",
          budget: job.budget,
          budgetPeriod: job.budgetPeriod || "Per Month",
          reportingLocation: job.reportingLocation || "-",
          numberOfRequirements: job.numberOfRequirements || 1,
          experienceRange: job.experienceRange || "-",
          monthsOfEngagement: job.monthsOfEngagement || "-",
          communication: job.communication || "-",
          primarySkills: job.primarySkills || [],
          secondarySkills: job.secondarySkills || [],
          tentativeStartDate: job.tentativeStartDate,
          createdAt: job.createdAt,
          customerOrganization: job.customerOrganization,
          description: job.description || "",
        });
      }

      if (hiringResponse.data?.hiringRecords) {
        const hiredTalents = hiringResponse.data.hiringRecords.map((record) => ({
          id: record.id,
          talentId: record.talentId,
          name: record.name,
          role: record.role,
          skills: record.skills || [],
          experience: record.experience,
          monthlyRate: formatCurrency(record.partnerRate),
          partnerOrg: record.partnerOrganization,
          hiredOn: formatDate(record.createdAt),
          startDate: formatDate(record.onboardingDate),
          contractEnd: formatDate(record.lastWorkingDay),
          status: record.status === "active" ? "Active" : record.status === "inactive" ? "Inactive" : "Terminated",
          resumeUrl: record.resumeUrl || "",
        }));
        setTalents(hiredTalents);
      }
    } catch (error) {
      console.error("Error fetching job/hired data:", error);
      message.error("Failed to load job and hired talents data");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    if (jobId) {
      fetchJobAndHiredTalents();
    }
  }, [jobId, fetchJobAndHiredTalents]);

  if (loading) {
    return (
      <JobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <Spin indicator={<LoadingOutlined style={{ fontSize: 48 }} spin />} />
          <p style={{ marginTop: 16 }}>Loading hired talents...</p>
        </div>
      </JobDetailsWrapper>
    );
  }

  if (!jobData) {
    return (
      <JobDetailsWrapper>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <h2>Job not found</h2>
          <Button type="primary" onClick={() => navigate("/admin/job-requirments")}>
            Back to Job Requirements
          </Button>
        </div>
      </JobDetailsWrapper>
    );
  }

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      render: (name, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <a
            style={{ fontWeight: 500, color: "#1890ff", cursor: "pointer" }}
            onClick={() => navigate(`/admin/talent-profiles/details/${record.talentId}`, {
              state: { from: `/admin/job-requirments/hired-talents/${jobId}`, fromLabel: "Back to View Hired" }
            })}
          >
            {name}
          </a>
          {record.resumeUrl && (
            <Tooltip title="Download Resume">
              <DownloadOutlined
                style={{ color: "#52c41a", cursor: "pointer", fontSize: 14 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownloadResume(record.resumeUrl, name);
                }}
              />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      width: 150,
    },
    {
      title: "Primary Skills",
      dataIndex: "skills",
      key: "skills",
      width: 280,
      render: (skills) => (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
          {(skills || []).map((skill, index) => (
            <Tag key={index} color="cyan" style={{ margin: "2px 0" }}>
              {typeof skill === 'object' ? `${skill.skill} - ${skill.level}` : skill}
            </Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Monthly Rate",
      dataIndex: "monthlyRate",
      key: "monthlyRate",
      width: 120,
      render: (rate) => <span style={{ fontWeight: 500 }}>{rate}</span>,
    },
    {
      title: "Experience",
      dataIndex: "experience",
      key: "experience",
      width: 100,
    },
    {
      title: "Partner Org",
      dataIndex: "partnerOrg",
      key: "partnerOrg",
      width: 150,
      render: (org) => (
        <a
          style={{ color: "#1890ff", cursor: "pointer" }}
          onClick={() => navigate(`/admin/organization/${encodeURIComponent(org)}`, {
            state: { from: `/admin/job-requirments/hired-talents/${jobId}`, fromLabel: "Back to Hired Talents" }
          })}
        >
          {org}
        </a>
      ),
    },
    {
      title: "Hired On",
      dataIndex: "hiredOn",
      key: "hiredOn",
      width: 110,
    },
    {
      title: "Contract End",
      dataIndex: "contractEnd",
      key: "contractEnd",
      width: 120,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={status === "Active" ? "green" : "default"} icon={<CheckCircleOutlined />}>
          {status}
        </Tag>
      ),
    },
  ];

  return (
    <JobDetailsWrapper>
      <div className="header">
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate("/admin/job-requirments")}
          className="back-button"
        >
          Back to Job Requirements
        </Button>
        <div className="title-row">
          <h2>Hired Talents</h2>
        </div>
      </div>

      <Card className="job-summary-card">
        <div className="job-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div className="job-id">Job ID: {jobData.id}</div>
            <h2 className="job-title">{jobData.role}</h2>
            <div className="job-meta">
              <Tag color={getStatusColor(jobData.status)}>{jobData.status?.toUpperCase()}</Tag>
              <Tag color="blue">{jobData.engagementType}</Tag>
              <Tag color="cyan" icon={<EnvironmentOutlined />}>{jobData.reportingLocation}</Tag>
              <Tag color="green" icon={<TeamOutlined />}>Requirements: {jobData.numberOfRequirements}</Tag>
              <Tag color="purple" icon={<CheckCircleOutlined />}>Hired: {talents.length}</Tag>
              {jobData.customerOrganization && (
                <Tag 
                  color="magenta" 
                  style={{ cursor: "pointer" }}
                  onClick={() => navigate(`/admin/organization/${encodeURIComponent(jobData.customerOrganization)}`, {
                    state: { from: `/admin/job-requirments/hired-talents/${jobId}`, fromLabel: "Back to Hired Talents" }
                  })}
                >
                  Customer: {jobData.customerOrganization}
                </Tag>
              )}
            </div>
          </div>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/admin/job-requirments/job-full-details/${jobId}`)}
            style={{ 
              color: "#1890ff", 
              fontWeight: 500,
              fontSize: 14,
              padding: 0,
            }}
          >
            Detailed View
          </Button>
        </div>

        <div className="metrics-row">
          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#e6f7ff" }}>
              <DollarOutlined style={{ fontSize: 24, color: "#1890ff" }} />
            </div>
            <div className="metric-info">
              <h3>{formatCurrency(jobData.budget)}</h3>
              <p>{jobData.budgetPeriod}</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#fff7e6" }}>
              <CalendarOutlined style={{ fontSize: 24, color: "#fa8c16" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.monthsOfEngagement}</h3>
              <p>Engagement Duration</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#f6ffed" }}>
              <ClockCircleOutlined style={{ fontSize: 24, color: "#52c41a" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.experienceRange}</h3>
              <p>Experience Required</p>
            </div>
          </div>

          <div className="metric-box">
            <div className="metric-icon" style={{ background: "#fff1f0" }}>
              <CommentOutlined style={{ fontSize: 24, color: "#ff4d4f" }} />
            </div>
            <div className="metric-info">
              <h3>{jobData.communication}</h3>
              <p>Communication Level</p>
            </div>
          </div>
        </div>

        <Row gutter={[24, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Primary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.primarySkills || []).map((skill, index) => (
                  <Tag key={index} color="blue" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Secondary Skills:</span>
              <div style={{ marginTop: 4 }}>
                {(jobData.secondarySkills || []).map((skill, index) => (
                  <Tag key={index} color="green" style={{ marginBottom: 4 }}>
                    {skill}
                  </Tag>
                ))}
                {(!jobData.secondarySkills || jobData.secondarySkills.length === 0) && (
                  <span style={{ color: "#8c8c8c" }}>None specified</span>
                )}
              </div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Start Date:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.tentativeStartDate)}</div>
            </div>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <div style={{ marginBottom: 8 }}>
              <span style={{ fontWeight: 600, color: "#262626" }}>Created On:</span>
              <div style={{ marginTop: 4, fontWeight: 500 }}>{formatDate(jobData.createdAt)}</div>
            </div>
          </Col>
        </Row>
      </Card>

      <div className="profiles-table-card">
        <h3>Hired Talents ({talents.length})</h3>
        {talents.length > 0 ? (
          <Table
            columns={columns}
            dataSource={talents}
            rowKey="id"
            pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ["10", "20", "50", "100"], showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows` }}
            scroll={{ x: 1400 }}
          />
        ) : (
          <div style={{ textAlign: "center", padding: "40px", color: "#8c8c8c" }}>
            <p>No talents hired yet for this job.</p>
          </div>
        )}
      </div>
    </JobDetailsWrapper>
  );
};

export default AdminJobHiredTalents;
